import React from 'react';

import { SideNavBody } from '@atlaskit/navigation-system/layout/side-nav';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { SkeletonMenuItemNext } from '@atlaskit/side-nav-items/skeleton';

/**
 * How many menu item placeholders the skeleton renders.
 *
 * The real item count is data-driven and unknown until the query resolves, so
 * this is a fixed approximation of a typical main nav.
 */
const SIDE_NAV_SKELETON_ITEM_COUNT = 5;

/**
 * A placeholder for the global side navigation's main nav items, for consumers
 * to render while the side nav's data is still in flight.
 *
 * This renders the side nav's *contents* only. Place it inside the
 * `SideNav` layout element, where `GlobalSideNavigation` would otherwise go.
 *
 * It is built from the same layout components the real nav uses — `MenuList`
 * wrapping `MenuListItem`s — with `SkeletonMenuItemNext` standing in for the
 * `LinkMenuItem`/`ButtonMenuItem` that `NavItem` resolves to once the data
 * arrives. Reusing that layout means item height, padding and the icon slot come
 * from the real components rather than from numbers copied out of them, so the
 * skeleton cannot drift as the menu item design changes.
 *
 * `hasElemBefore` is set because the main nav items all render a leading icon.
 */
export function GSNSkeleton(): React.JSX.Element {
	return (
		<SideNavBody>
			<MenuList>
				{Array.from({ length: SIDE_NAV_SKELETON_ITEM_COUNT }, (_, index) => (
					<MenuListItem key={index}>
						<SkeletonMenuItemNext hasElemBefore />
					</MenuListItem>
				))}
			</MenuList>
		</SideNavBody>
	);
}
